'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

interface ComplianceNavigatorProps {
  currentLanguage: string;
  onSelectRegulation: (regulation: string | null) => void;
  onSelectIndustry: (industry: string | null) => void;
}

const ComplianceNavigator = ({
  currentLanguage,
  onSelectRegulation,
  onSelectIndustry,
}: ComplianceNavigatorProps) => {
  const [activeTab, setActiveTab] = useState<'regulation' | 'industry'>('regulation');

  const regulations = [
    {
      id: 'brsr-sebi',
      name: 'BRSR SEBI India',
      icon: 'DocumentTextIcon',
      description: 'Business Responsibility and Sustainability Reporting framework for Indian companies',
      color: 'primary',
      features: ['Core Indicators', 'Leadership Indicators', 'Sector-Specific', 'Value Chain'],
      deadline: 'Annual FY Reporting',
    },
    {
      id: 'csrd-europe',
      name: 'CSRD Europe',
      icon: 'GlobeEuropeAfricaIcon',
      description: 'Corporate Sustainability Reporting Directive for European operations',
      color: 'secondary',
      features: ['Double Materiality', 'ESRS Standards', 'Supply Chain', 'Climate Adaptation'],
      deadline: 'Phased Implementation 2024-2028',
    },
    {
      id: 'brsr-lite',
      name: 'BRSR Lite',
      icon: 'DocumentCheckIcon',
      description: 'Simplified reporting for value chain partners and SMEs',
      color: 'accent',
      features: ['Essential Metrics', 'Quick Filing', 'Supplier Alignment', 'NetZero Ready'],
      deadline: 'Voluntary Reporting',
    },
  ];

  const industries = [
    {
      id: 'banking-fibac',
      name: 'Banking - FIBAC',
      icon: 'BuildingLibraryIcon',
      description: 'Financial Industry Banking and Climate framework',
      metrics: ['Climate Risk', 'Green Lending', 'Financed Emissions', 'Transition Plans'],
      color: 'primary',
    },
    {
      id: 'power-sector',
      name: 'Power Sector',
      icon: 'BoltIcon',
      description: 'Low emission tracing via AI/IoT for power generation',
      metrics: ['Emission Tracking', 'Renewable Mix', 'Grid Efficiency', 'Smart Monitoring'],
      color: 'warning',
    },
    {
      id: 'oil-gas',
      name: 'Oil & Gas',
      icon: 'FireIcon',
      description: 'Upstream and downstream emission monitoring',
      metrics: ['Methane Leaks', 'Flaring Data', 'Carbon Intensity', 'Scope 3 Tracking'],
      color: 'error',
    },
    {
      id: 'utilities',
      name: 'Public Utilities',
      icon: 'HomeModernIcon',
      description: 'Port building, desert rejuvenation, water conservation',
      metrics: ['Water Usage', 'Waste Management', 'Energy Efficiency', 'Community Impact'],
      color: 'info',
    },
  ];

  return (
    <section id="compliance-navigator" className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
            Navigate Your Compliance Journey
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Select your regulatory framework or industry sector to access tailored compliance pathways
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-card border border-border rounded-lg p-1">
            <button
              onClick={() => {
                setActiveTab('regulation');
                onSelectIndustry(null);
              }}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                activeTab === 'regulation' ?'bg-primary text-primary-foreground shadow-natural' :'text-text-secondary hover:text-text-primary'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name="DocumentTextIcon" size={20} />
                <span>By Regulation</span>
              </div>
            </button>
            <button
              onClick={() => {
                setActiveTab('industry');
                onSelectRegulation(null);
              }}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                activeTab === 'industry' ?'bg-primary text-primary-foreground shadow-natural' :'text-text-secondary hover:text-text-primary'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name="BuildingOfficeIcon" size={20} />
                <span>By Industry</span>
              </div>
            </button>
          </div>
        </div>

        {/* Regulations View */}
        {activeTab === 'regulation' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regulations.map((regulation) => (
              <div
                key={regulation.id}
                onClick={() => onSelectRegulation(regulation.id)}
                className="bg-card border-2 border-border rounded-xl p-6 hover:border-primary hover:shadow-natural-lg transition-all duration-300 cursor-pointer group"
              >
                <div className={`inline-flex p-3 bg-${regulation.color}/10 rounded-lg mb-4`}>
                  <Icon name={regulation.icon} size={32} className={`text-${regulation.color}`} />
                </div>

                <h3 className="font-heading font-bold text-xl text-text-primary mb-2 group-hover:text-primary transition-colors">
                  {regulation.name}
                </h3>

                <p className="text-sm text-text-secondary mb-4">{regulation.description}</p>

                <div className="space-y-2 mb-4">
                  {regulation.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <Icon name="CheckIcon" size={16} className="text-success" />
                      <span className="text-sm text-text-primary">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t border-border">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-text-secondary">{regulation.deadline}</span>
                    <Icon
                      name="ArrowRightIcon"
                      size={20}
                      className="text-primary group-hover:translate-x-1 transition-transform"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Industries View */}
        {activeTab === 'industry' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {industries.map((industry) => (
              <div
                key={industry.id}
                onClick={() => onSelectIndustry(industry.id)}
                className="bg-card border-2 border-border rounded-xl p-6 hover:border-primary hover:shadow-natural-lg transition-all duration-300 cursor-pointer group"
              >
                <div className="flex items-start space-x-4">
                  <div className={`flex-shrink-0 p-3 bg-${industry.color}/10 rounded-lg`}>
                    <Icon name={industry.icon} size={32} className={`text-${industry.color}`} />
                  </div>

                  <div className="flex-grow">
                    <h3 className="font-heading font-bold text-xl text-text-primary mb-2 group-hover:text-primary transition-colors">
                      {industry.name}
                    </h3>
                    <p className="text-sm text-text-secondary mb-4">{industry.description}</p>

                    <div className="grid grid-cols-2 gap-2">
                      {industry.metrics.map((metric, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <Icon name="ChartBarIcon" size={14} className="text-primary" />
                          <span className="text-xs text-text-primary">{metric}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Icon
                    name="ArrowRightIcon"
                    size={24}
                    className="text-primary group-hover:translate-x-1 transition-transform flex-shrink-0"
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-12 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-card border border-border rounded-xl p-6">
            <div className="text-left">
              <p className="font-semibold text-text-primary mb-1">Need Custom Compliance Guidance?</p>
              <p className="text-sm text-text-secondary">Our experts can help you navigate complex requirements</p>
            </div>
            <Link
              href="/get-started"
              className="whitespace-nowrap px-6 py-3 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary/90 transition-all duration-300"
            >
              Consult Expert
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComplianceNavigator;